import React, { useState, useRef } from "react";
import ReactDOM from "react-dom";
import Button from "../Button/Button";
import ChromePicker from "../ui/LazyChromePicker";
import { 
  CheckIcon, 
  ChevronRight, 
  ChevronLeft,
  Facebook, 
  Linkedin, 
  Globe, 
  Share2,
  QrCode,
  Code,
  LayoutGrid,
  MessageSquare,
  MousePointer,
  Copy,
  X,
  Monitor,
  Smartphone,
  Eye,
  ArrowLeft
} from "lucide-react";

const escapeForJS = (str) => {
  if (!str) return '';
  return String(str)
    .replace(/\\/g, '\\\\')
    .replace(/"/g, '\\"')
    .replace(/'/g, "\\'")
    .replace(/\n/g, '\\n')
    .replace(/\r/g, '\\r')
    .replace(/<\/script/gi, '<\\/script');
};

const escapeForHTML = (str) => {
  if (!str) return '';
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
};

// Resolve the survey URL that goes into generated embed snippets. In a real
// deployment the embed must point at the public production domain, but while
// developing locally we keep the localhost URL so the generated code can be
// tested against the local server instead of hitting production.
const resolveEmbedSurveyUrl = (surveyLink) => {
  if (!surveyLink) return '';
  const isLocalDev =
    typeof window !== 'undefined' &&
    (window.location.hostname === 'localhost' ||
      window.location.hostname === '127.0.0.1');
  if (isLocalDev) return surveyLink;
  return surveyLink.replace('http://localhost:3000', 'https://app.pollbolt.com');
};

// Full-screen Iframe Settings Modal (rendered via portal)
const IframeSettingsModal = ({ isOpen, onClose, surveyLink }) => {
  const [useFixedDimensions, setUseFixedDimensions] = useState(true);
  const [width, setWidth] = useState(800);
  const [height, setHeight] = useState(600);
  const [widthPercent, setWidthPercent] = useState(100);
  const [heightPercent, setHeightPercent] = useState(100);
  const [isCopied, setIsCopied] = useState(false);

  const embedCode = useFixedDimensions 
    ? `<iframe frameborder="0" src="${escapeForHTML(surveyLink)}" height="${height}" width="${width}"></iframe>`
    : `<iframe frameborder="0" src="${escapeForHTML(surveyLink)}" height="${heightPercent}%" width="${widthPercent}%"></iframe>`;

  const handleCopy = async () => {
    await navigator.clipboard.writeText(embedCode);
    setIsCopied(true);
    setTimeout(() => setIsCopied(false), 2000);
  };

  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-[10003] flex">
      {/* Left side - Settings (white background) - full width on mobile, 2/3 on desktop */}
      <div className="w-full md:w-2/3 bg-white flex flex-col relative">
        {/* Back button - top left corner */}
        <div className="absolute top-4 md:top-6 left-4 md:left-6 z-10">
          <button
            onClick={onClose}
            className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <ChevronLeft className="w-5 h-5 md:w-6 md:h-6 text-gray-600" />
          </button>
        </div>

        {/* Close button - top right corner on mobile only */}
        <div className="absolute top-4 right-4 z-10 md:hidden">
          <button
            onClick={onClose}
            className="w-10 h-10 flex items-center justify-center bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
          >
            <X className="w-5 h-5 text-gray-600" />
          </button>
        </div>
        
        {/* Content - centered with max-width, offset right and down */}
        <div className="flex-1 overflow-y-auto flex justify-center pt-24 md:pt-44 px-4 md:px-0">
          <div className="w-full max-w-2xl md:px-8 md:ml-32">
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6 md:mb-8">Iframe settings</h1>
            
            {/* Fixed dimensions toggle and inputs */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6 pb-6 border-b border-gray-200">
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setUseFixedDimensions(!useFixedDimensions)}
                  className={`relative w-10 h-5 rounded-full transition-colors flex-shrink-0 ${
                    useFixedDimensions ? 'bg-blue-600' : 'bg-gray-300'
                  }`}
                >
                  <span
                    className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform duration-200 ${
                      useFixedDimensions ? 'translate-x-5' : 'translate-x-0'
                    }`}
                  />
                </button>
                <span className="text-sm font-medium text-gray-800">Iframe fixed dimensions</span>
              </div>
              
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-500">Width</span>
                  <div className="flex items-center border border-gray-300 rounded-md overflow-hidden">
                    <input
                      type="number"
                      value={useFixedDimensions ? width : widthPercent}
                      onChange={(e) => useFixedDimensions ? setWidth(Number(e.target.value)) : setWidthPercent(Number(e.target.value))}
                      className="w-12 px-1 py-1 text-sm text-center border-none focus:outline-none focus:ring-0"
                    />
                    <span className="px-1.5 py-1 text-sm text-gray-500 bg-gray-50 border-l border-gray-300">
                      {useFixedDimensions ? 'px' : '%'}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-500">Height</span>
                  <div className="flex items-center border border-gray-300 rounded-md overflow-hidden">
                    <input
                      type="number"
                      value={useFixedDimensions ? height : heightPercent}
                      onChange={(e) => useFixedDimensions ? setHeight(Number(e.target.value)) : setHeightPercent(Number(e.target.value))}
                      className="w-12 px-1 py-1 text-sm text-center border-none focus:outline-none focus:ring-0"
                    />
                    <span className="px-1.5 py-1 text-sm text-gray-500 bg-gray-50 border-l border-gray-300">
                      {useFixedDimensions ? 'px' : '%'}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Instructions */}
            <div className="mb-8">
              <p className="text-sm text-gray-600 mb-3">
                Insert the code to the HTML of your web page
              </p>
              
              {/* Code block */}
              <div className="bg-gray-900 rounded-lg p-4">
                <button
                  onClick={handleCopy}
                  className="flex items-center gap-2 text-blue-400 hover:text-blue-300 transition-colors mb-2"
                >
                  {isCopied ? (
                    <>
                      <CheckIcon className="w-4 h-4" />
                      <span className="text-sm font-medium">Copied!</span>
                    </>
                  ) : (
                    <>
                      <Copy className="w-4 h-4" />
                      <span className="text-sm font-medium">Copy</span>
                    </>
                  )}
                </button>
                <div className="max-h-24 overflow-y-auto">
                  <pre className="text-gray-300 text-sm whitespace-pre-wrap break-all font-mono leading-relaxed">
                    {embedCode}
                  </pre>
                </div>
              </div>
            </div>

            {/* Back button */}
            <button
              onClick={onClose}
              className="px-8 py-3.5 border border-gray-300 rounded-lg text-base font-medium text-gray-700 hover:bg-gray-50 transition-colors mb-8"
            >
              Back
            </button>
          </div>
        </div>
      </div>

      {/* Right side - Preview/Illustration (Pro plan gradient) - hidden on mobile, 1/3 on desktop */}
      <div className="hidden md:flex w-1/3 bg-gradient-to-b from-[#EFFBFF] to-[#FEE135] flex-col relative">
        {/* Close button */}
        <div className="absolute top-6 right-6 z-10">
          <button
            onClick={onClose}
            className="w-12 h-12 flex items-center justify-center bg-black/5 backdrop-blur-sm rounded-xl hover:bg-black/10 transition-colors"
          >
            <X className="w-6 h-6 text-gray-700" />
          </button>
        </div>
        
        {/* Illustration - centered in the middle of the panel */}
        <div className="flex-1 flex flex-col items-center justify-center px-8 py-12">
          <div className="w-full max-w-[200px]">
            {/* Laptop mockup */}
            <div className="bg-gray-800 rounded-t-xl p-2 w-full">
              <div className="bg-white rounded-md h-28 flex items-center justify-center">
                <div className="text-center">
                  <div className="flex items-center justify-center gap-2 mb-2">
                    <Monitor className="w-4 h-4 text-gray-400" />
                    <Smartphone className="w-3 h-3 text-gray-400" />
                  </div>
                  <div className="border-2 border-dashed border-gray-300 rounded p-2.5 mx-3">
                    <Code className="w-5 h-5 text-blue-500 mx-auto mb-0.5" />
                    <span className="text-[10px] text-gray-500">&lt;iframe&gt;</span>
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-gray-700 rounded-b-xl h-2 w-full"></div>
            <div className="bg-gray-600 h-1.5 w-4/5 mx-auto rounded-b"></div>
          </div>
          
          {/* Description - below laptop */}
          <div className="mt-6 text-gray-700 text-center max-w-[240px]">
            <p className="text-xs leading-relaxed">
              Iframe brings up an option to display a survey as an integral part of your website content. 
              It is possible to adjust frame dimensions so that it fully fits the layout.
            </p>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

// Full-screen Widget Settings Modal (rendered via portal)
const WidgetSettingsModal = ({ isOpen, onClose, surveyLink }) => {
  const [buttonText, setButtonText] = useState('Tell us what you think');
  const [buttonColor, setButtonColor] = useState('#38BDF8');
  const [isCopied, setIsCopied] = useState(false);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [pickerColor, setPickerColor] = useState('#38BDF8');
  const [colorPickerPosition, setColorPickerPosition] = useState({ top: 0, left: 0 });
  const [showPreview, setShowPreview] = useState(false);
  const [showWidgetPanel, setShowWidgetPanel] = useState(false);

  // Use the production URL in deployed builds, but keep localhost while developing.
  const productionSurveyUrl = resolveEmbedSurveyUrl(surveyLink);
  
  const embedCode = `<!-- PollBolt Widget -->
<script>
(function() {
  var config = {
    surveyUrl: "${escapeForJS(productionSurveyUrl)}",
    buttonText: "${escapeForJS(buttonText)}",
    buttonColor: "${escapeForJS(buttonColor)}",
    textColor: "#ffffff"
  };
  
  // Create container that holds both tab and panel
  var container = document.createElement('div');
  container.id = 'pollbolt-widget-container';
  container.style.cssText = 'position:fixed;right:-320px;top:50%;transform:translateY(-50%);display:flex;z-index:9999;transition:right 0.3s ease;';
  
  // Create widget tab (on the left side of panel)
  var tab = document.createElement('div');
  tab.id = 'pollbolt-widget-tab';
  tab.style.cssText = 'display:flex;flex-direction:column;align-items:center;cursor:pointer;border-radius:0 8px 8px 0;overflow:hidden;align-self:flex-end;margin-bottom:16px;box-shadow:0 2px 10px rgba(0,0,0,0.15);background:' + config.buttonColor + ';transform:rotate(180deg);';
  
  // Create close button (inside tab, visible when open)
  var closeBtn = document.createElement('button');
  closeBtn.id = 'pollbolt-close';
  closeBtn.innerHTML = '&times;';
  closeBtn.style.cssText = 'display:none;padding:8px;background:none;border:none;cursor:pointer;color:rgba(255,255,255,0.8);font-size:16px;line-height:1;';
  closeBtn.onmouseover = function() { this.style.color = '#fff'; };
  closeBtn.onmouseout = function() { this.style.color = 'rgba(255,255,255,0.8)'; };
  
  // Create text element
  var tabText = document.createElement('div');
  tabText.textContent = config.buttonText;
  tabText.style.cssText = 'color:' + config.textColor + ';padding:20px 10px;font-family:system-ui,-apple-system,sans-serif;font-size:16px;font-weight:500;writing-mode:vertical-rl;';
  
  tab.appendChild(closeBtn);
  tab.appendChild(tabText);
  
  // Create panel (no header, just iframe)
  var panel = document.createElement('div');
  panel.id = 'pollbolt-widget-panel';
  panel.style.cssText = 'width:320px;height:600px;background:#fff;box-shadow:-5px 0 25px rgba(0,0,0,0.15);border-radius:8px 0 0 8px;overflow:hidden;';
  panel.innerHTML = '<iframe src="' + config.surveyUrl + '" style="width:100%;height:100%;border:none;"></iframe>';
  
  container.appendChild(tab);
  container.appendChild(panel);
  document.body.appendChild(container);
  
  var isOpen = false;
  function toggleWidget() {
    isOpen = !isOpen;
    container.style.right = isOpen ? '0' : '-320px';
    closeBtn.style.display = isOpen ? 'block' : 'none';
  }
  
  tabText.onclick = function() { if (!isOpen) toggleWidget(); };
  tab.onclick = function(e) { if (e.target === tab && !isOpen) toggleWidget(); };
  closeBtn.onclick = function(e) { e.stopPropagation(); if (isOpen) toggleWidget(); };
})();
</script>`;

  const handleCopy = async () => {
    await navigator.clipboard.writeText(embedCode);
    setIsCopied(true);
    setTimeout(() => setIsCopied(false), 2000);
  };

  const handleOpenColorPicker = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setPickerColor(buttonColor);
    setColorPickerPosition({
      top: rect.bottom + 8,
      left: rect.right - 280
    });
    setShowColorPicker(true);
  };

  const handleColorConfirm = () => {
    if (/^#[0-9A-F]{6}$/i.test(pickerColor)) {
      setButtonColor(pickerColor);
    }
    setShowColorPicker(false);
  };

  if (!isOpen) return null;

  // Preview Mode
  if (showPreview) {
    return ReactDOM.createPortal(
      <div className="fixed inset-0 z-[10003] bg-white flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6">
          <button
            onClick={() => setShowPreview(false)}
            className="w-12 h-12 flex items-center justify-center border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <ChevronLeft className="w-6 h-6 text-gray-600" />
          </button>
          <button
            onClick={onClose}
            className="w-12 h-12 flex items-center justify-center border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <X className="w-6 h-6 text-gray-600" />
          </button>
        </div>

        {/* Preview Content */}
        <div className="flex-1 flex flex-col items-center justify-center relative">
          {/* Eye Icon - larger and lighter */}
          <div className="mb-8">
            <Eye className="w-20 h-20 text-gray-200" />
          </div>
          
          <h2 className="text-2xl font-semibold text-gray-600 mb-3">Pop-over widget preview</h2>
          <p className="text-sm text-gray-400 text-center max-w-md mb-6">
            This is how the user will view your survey. You can click it through back and forth in 
            preview mode to check its functionality and accuracy. Submitted answers are not saved.
          </p>
          
          <button
            onClick={() => setShowPreview(false)}
            className="flex items-center gap-2 text-gray-500 hover:text-gray-700 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm font-medium">Back to settings</span>
          </button>

          {/* Widget with Tab attached - like Survio */}
          <div 
            className="fixed top-1/2 -translate-y-1/2 flex transition-all duration-300 ease-in-out"
            style={{ right: showWidgetPanel ? '0' : '-320px' }}
          >
            {/* Tab - attached to panel */}
            <div 
              className="flex flex-col items-center cursor-pointer shadow-lg rounded-r-lg overflow-hidden self-end mb-4"
              style={{ backgroundColor: buttonColor, transform: 'rotate(180deg)' }}
              onClick={() => setShowWidgetPanel(!showWidgetPanel)}
            >
              {showWidgetPanel && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowWidgetPanel(false);
                  }}
                  className="p-2 text-white/80 hover:text-white transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
              <div 
                className="text-white text-base font-medium py-5 px-2.5"
                style={{ writingMode: 'vertical-rl' }}
              >
                {buttonText}
              </div>
            </div>
            
            {/* Survey Panel - no header */}
            <div className="w-80 h-[600px] bg-white shadow-2xl rounded-l-lg overflow-hidden">
              <iframe 
                src={`${surveyLink}?preview=1`}
                className="w-full h-full border-0"
                title="Survey Preview"
              />
            </div>
          </div>
        </div>
      </div>,
      document.body
    );
  }

  // Settings Mode
  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-[10003] flex">
      {/* Left side - Settings (white background) - full width on mobile, 2/3 on desktop */}
      <div className="w-full md:w-2/3 bg-white flex flex-col relative">
        {/* Back button - top left corner */}
        <div className="absolute top-4 md:top-6 left-4 md:left-6 z-10">
          <button
            onClick={onClose}
            className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <ChevronLeft className="w-5 h-5 md:w-6 md:h-6 text-gray-600" />
          </button>
        </div>

        {/* Close button - top right corner on mobile only */}
        <div className="absolute top-4 right-4 z-10 md:hidden">
          <button
            onClick={onClose}
            className="w-10 h-10 flex items-center justify-center bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
          >
            <X className="w-5 h-5 text-gray-600" />
          </button>
        </div>
        
        {/* Content - centered with max-width, offset right and down */}
        <div className="flex-1 overflow-y-auto flex justify-center pt-24 md:pt-44 px-4 md:px-0">
          <div className="w-full max-w-2xl md:px-8 md:ml-32">
            <div className="flex items-center gap-3 mb-6 md:mb-8">
              <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Pop-over widget</h1>
              <span className="px-2 py-0.5 bg-emerald-500 text-white text-xs font-semibold rounded">NEW</span>
            </div>
            
            {/* Button text */}
            <div className="flex items-center justify-between mb-6 pb-6 border-b border-gray-200">
              <span className="text-sm font-medium text-gray-800">Button text</span>
              <div className="relative">
                <input
                  type="text"
                  value={buttonText}
                  onChange={(e) => setButtonText(e.target.value.slice(0, 40))}
                  className="w-56 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent pr-12"
                  maxLength={40}
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">
                  {buttonText.length}/40
                </span>
              </div>
            </div>

            {/* Button color */}
            <div className="flex items-center justify-between mb-6 pb-6 border-b border-gray-200">
              <span className="text-sm font-medium text-gray-800">Button color</span>
              <button
                onClick={handleOpenColorPicker}
                className="w-10 h-8 rounded cursor-pointer border border-gray-300 hover:border-gray-400 transition-colors"
                style={{ backgroundColor: buttonColor }}
                aria-label="Pick button color"
              />
            </div>

            {/* Color Picker Popup - Same as brandkit */}
            {showColorPicker && (
              <>
                <div 
                  className="fixed inset-0 z-40" 
                  onClick={() => setShowColorPicker(false)}
                />
                <div 
                  className="fixed z-50" 
                  style={{ top: `${colorPickerPosition.top}px`, left: `${colorPickerPosition.left}px` }}
                >
                  <div className="widget-color-picker w-[280px] p-3 bg-white rounded-xl shadow-lg border border-gray-200">
                    <style>{`
                      .widget-color-picker .chrome-picker input[type='text'] { display: none !important; }
                      .widget-color-picker .chrome-picker input[type='number'] { display: none !important; }
                      .widget-color-picker .chrome-picker label { display: none !important; }
                      .widget-color-picker .chrome-picker .chrome-fields { display: none !important; }
                      .widget-color-picker .chrome-picker .flexbox-fix input { display: none !important; }
                      .widget-color-picker .chrome-picker .flexbox-fix svg { display: none !important; }
                      .widget-color-picker .chrome-picker .flexbox-fix { padding-bottom: 0 !important; margin-bottom: 0 !important; }
                      .widget-color-picker .chrome-picker .hue-horizontal { margin-bottom: 0 !important; }
                      .widget-color-picker .chrome-picker > div:last-child { margin-bottom: 0 !important; padding-bottom: 0 !important; }
                      .widget-color-picker .chrome-picker { margin-bottom: 0 !important; padding-bottom: 0 !important; border-radius: 8px !important; overflow: hidden !important; }
                      .widget-color-picker .chrome-picker .chrome-body { padding-bottom: 0 !important; margin-bottom: 0 !important; }
                      .widget-color-picker .chrome-picker .saturation-relative { border-radius: 8px !important; overflow: hidden !important; }
                      .widget-color-picker .chrome-picker .saturation-relative * { border-radius: 8px !important; }
                      .widget-color-picker .chrome-picker .saturation-white { border-radius: 8px !important; }
                      .widget-color-picker .chrome-picker .saturation-black { border-radius: 8px !important; }
                      .widget-color-picker .chrome-picker div[style*="background"] { border-radius: 8px !important; }
                    `}</style>
                    <ChromePicker
                      disableAlpha={true}
                      styles={{
                        default: {
                          picker: { boxShadow: 'none', width: '100%' },
                          fields: { display: 'none' },
                          color: { display: 'none' },
                          activeColor: { display: 'none' },
                          hue: { marginTop: 0, marginBottom: 0 },
                          body: { paddingBottom: 0 },
                        },
                      }}
                      color={pickerColor}
                      onChange={(c) => {
                        const { r, g, b } = c.rgb || {};
                        const toHex = (n) => n.toString(16).padStart(2, '0');
                        const hex = `#${toHex(r)}${toHex(g)}${toHex(b)}`;
                        setPickerColor(hex);
                      }}
                    />
                    <div className="mt-1 flex items-center gap-2">
                      <div className="text-sm text-gray-600">Hex</div>
                      <input
                        className="ml-auto text-black text-sm px-3 py-2 border border-gray-200 rounded-md focus:ring-2 focus:ring-black focus:border-black text-left max-w-[180px]"
                        value={(pickerColor || '#ffffff').toUpperCase()}
                        onChange={(e) => {
                          const value = e.target.value.startsWith('#') ? e.target.value : `#${e.target.value}`;
                          setPickerColor(value);
                        }}
                      />
                    </div>
                    <button
                      onClick={handleColorConfirm}
                      disabled={!/^#[0-9A-F]{6}$/i.test(pickerColor)}
                      className="w-full mt-3 px-4 py-2 bg-black text-white text-sm font-medium rounded-lg hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Add Color
                    </button>
                  </div>
                </div>
              </>
            )}

            {/* Instructions */}
            <div className="mb-8">
              <p className="text-sm text-gray-600 mb-3">
                Insert the code to the HTML of your web page
              </p>
              
              {/* Code block */}
              <div className="bg-gray-900 rounded-lg p-4">
                <button
                  onClick={handleCopy}
                  className="flex items-center gap-2 text-blue-400 hover:text-blue-300 transition-colors mb-2"
                >
                  {isCopied ? (
                    <>
                      <CheckIcon className="w-4 h-4" />
                      <span className="text-sm font-medium">Copied!</span>
                    </>
                  ) : (
                    <>
                      <Copy className="w-4 h-4" />
                      <span className="text-sm font-medium">Copy</span>
                    </>
                  )}
                </button>
                <div className="max-h-24 overflow-y-auto">
                  <pre className="text-gray-300 text-sm whitespace-pre-wrap break-all font-mono leading-relaxed">
                    {embedCode}
                  </pre>
                </div>
              </div>
            </div>

            {/* Buttons row */}
            <div className="flex items-center gap-3 mb-8">
              <button
                onClick={onClose}
                className="px-8 py-3.5 border border-gray-300 rounded-lg text-base font-medium text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Back
              </button>
              <button
                onClick={() => setShowPreview(true)}
                className="px-8 py-3.5 border border-gray-300 rounded-lg text-base font-medium text-gray-700 hover:bg-gray-50 transition-colors flex items-center gap-2"
              >
                <Eye className="w-5 h-5" />
                Preview
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Right side - Preview/Illustration (Pro plan gradient) - hidden on mobile, 1/3 on desktop */}
      <div className="hidden md:flex w-1/3 bg-gradient-to-b from-[#EFFBFF] to-[#FEE135] flex-col relative">
        {/* Close button */}
        <div className="absolute top-6 right-6 z-10">
          <button
            onClick={onClose}
            className="w-12 h-12 flex items-center justify-center bg-black/5 backdrop-blur-sm rounded-xl hover:bg-black/10 transition-colors"
          >
            <X className="w-6 h-6 text-gray-700" />
          </button>
        </div>
        
        {/* Illustration - centered in the middle of the panel */}
        <div className="flex-1 flex flex-col items-center justify-center px-8 py-12">
          <div className="w-full max-w-[200px]">
            {/* Laptop mockup */}
            <div className="bg-gray-800 rounded-t-xl p-2 w-full">
              <div className="bg-white rounded-md h-28 flex items-center justify-center relative">
                <div className="text-center">
                  <div className="flex items-center justify-center gap-2 mb-2">
                    <Monitor className="w-4 h-4 text-gray-400" />
                    <Smartphone className="w-3 h-3 text-gray-400" />
                  </div>
                  <div className="border-2 border-dashed border-gray-300 rounded p-2.5 mx-3">
                    <MessageSquare className="w-5 h-5 text-blue-500 mx-auto mb-0.5" />
                    <span className="text-[10px] text-gray-500">Widget</span>
                  </div>
                </div>
                {/* Widget tab preview */}
                <div 
                  className="absolute -right-2 top-1/2 -translate-y-1/2 text-[8px] text-white py-3 px-1 rounded-l"
                  style={{ backgroundColor: buttonColor, writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}
                >
                  {buttonText.slice(0, 15)}...
                </div>
              </div>
            </div>
            <div className="bg-gray-700 rounded-b-xl h-2 w-full"></div>
            <div className="bg-gray-600 h-1.5 w-4/5 mx-auto rounded-b"></div>
          </div>
          
          {/* Description - below laptop */}
          <div className="mt-6 text-gray-700 text-center max-w-[240px]">
            <p className="text-xs leading-relaxed">
              Pop-over widget enables direct access to a survey which is displayed in an interactive 
              bubble after clicking the tab on the right side of the screen.
            </p>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

// Full-screen Button Settings Modal (rendered via portal)
const ButtonSettingsModal = ({ isOpen, onClose, surveyLink }) => {
  const [buttonText, setButtonText] = useState('Start Survey NOW!');
  const [backgroundColor, setBackgroundColor] = useState('#4F46E5');
  const [fontColor, setFontColor] = useState('#FFFFFF');
  const [borderRadius, setBorderRadius] = useState(50); // Default: very rounded (pill shape)
  const [openInNewWindow, setOpenInNewWindow] = useState(true);
  const [isCopied, setIsCopied] = useState(false);
  
  // Color picker states
  const [showBgColorPicker, setShowBgColorPicker] = useState(false);
  const [showFontColorPicker, setShowFontColorPicker] = useState(false);
  const [pickerColor, setPickerColor] = useState('#4F46E5');
  const [colorPickerPosition, setColorPickerPosition] = useState({ top: 0, left: 0 });
  const [activeColorPicker, setActiveColorPicker] = useState(null); // 'bg' or 'font'

  // Use the production URL in deployed builds, but keep localhost while developing.
  const productionSurveyUrl = resolveEmbedSurveyUrl(surveyLink);
  
  const embedCode = `<a href="${escapeForHTML(productionSurveyUrl)}"${openInNewWindow ? ' target="_blank"' : ''} style="display:inline-block;text-decoration:none;background-color:${escapeForHTML(backgroundColor)};color:${escapeForHTML(fontColor)};font-family:Arial, Helvetica, sans-serif;-webkit-border-radius:${borderRadius}px;-moz-border-radius:${borderRadius}px;border-radius:${borderRadius}px;padding:11px 25px;font-size:14px;line-height:180%;font-weight:bold;letter-spacing:0.5px">${escapeForHTML(buttonText)}</a>`;

  const handleCopy = async () => {
    await navigator.clipboard.writeText(embedCode);
    setIsCopied(true);
    setTimeout(() => setIsCopied(false), 2000);
  };

  const handleOpenColorPicker = (e, type) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setPickerColor(type === 'bg' ? backgroundColor : fontColor);
    setColorPickerPosition({
      top: rect.bottom + 8,
      left: rect.right - 280
    });
    setActiveColorPicker(type);
    if (type === 'bg') {
      setShowBgColorPicker(true);
      setShowFontColorPicker(false);
    } else {
      setShowFontColorPicker(true);
      setShowBgColorPicker(false);
    }
  };

  const handleColorConfirm = () => {
    if (/^#[0-9A-F]{6}$/i.test(pickerColor)) {
      if (activeColorPicker === 'bg') {
        setBackgroundColor(pickerColor);
      } else {
        setFontColor(pickerColor);
      }
    }
    setShowBgColorPicker(false);
    setShowFontColorPicker(false);
    setActiveColorPicker(null);
  };

  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-[10003] flex">
      {/* Left side - Settings (white background) - full width on mobile, 2/3 on desktop */}
      <div className="w-full md:w-2/3 bg-white flex flex-col relative">
        {/* Back button - top left corner */}
        <div className="absolute top-4 md:top-6 left-4 md:left-6 z-10">
          <button
            onClick={onClose}
            className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <ChevronLeft className="w-5 h-5 md:w-6 md:h-6 text-gray-600" />
          </button>
        </div>

        {/* Close button - top right corner on mobile only */}
        <div className="absolute top-4 right-4 z-10 md:hidden">
          <button
            onClick={onClose}
            className="w-10 h-10 flex items-center justify-center bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
          >
            <X className="w-5 h-5 text-gray-600" />
          </button>
        </div>
        
        {/* Content - centered with max-width, offset right and down */}
        <div className="flex-1 overflow-y-auto flex justify-center pt-20 md:pt-24 px-4 md:px-0">
          <div className="w-full max-w-2xl md:px-8 md:ml-32">
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6">Button</h1>
            
            {/* Button Preview */}
            <div className="mb-5 p-5 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center">
              <a
                href="#"
                onClick={(e) => e.preventDefault()}
                style={{
                  display: 'inline-block',
                  textDecoration: 'none',
                  backgroundColor: backgroundColor,
                  color: fontColor,
                  fontFamily: 'Arial, Helvetica, sans-serif',
                  borderRadius: `${borderRadius}px`,
                  padding: '11px 25px',
                  fontSize: '14px',
                  lineHeight: '180%',
                  fontWeight: 'bold',
                  letterSpacing: '0.5px'
                }}
              >
                {buttonText}
              </a>
            </div>

            {/* Button text */}
            <div className="flex items-center justify-between mb-4 pb-4 border-b border-gray-200">
              <span className="text-sm font-medium text-gray-800">Button text</span>
              <div className="relative">
                <input
                  type="text"
                  value={buttonText}
                  onChange={(e) => setButtonText(e.target.value.slice(0, 40))}
                  className="w-56 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent pr-12"
                  maxLength={40}
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">
                  {buttonText.length}/40
                </span>
              </div>
            </div>

            {/* Colors and corners row */}
            <div className="flex items-center justify-between mb-4 pb-4 border-b border-gray-200">
              <div className="flex items-center gap-5">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-800">Background</span>
                  <button
                    onClick={(e) => handleOpenColorPicker(e, 'bg')}
                    className="w-8 h-7 rounded cursor-pointer border border-gray-300 hover:border-gray-400 transition-colors"
                    style={{ backgroundColor: backgroundColor }}
                    aria-label="Pick background color"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-800">Font</span>
                  <button
                    onClick={(e) => handleOpenColorPicker(e, 'font')}
                    className="w-8 h-7 rounded cursor-pointer border border-gray-300 hover:border-gray-400 transition-colors"
                    style={{ backgroundColor: fontColor }}
                    aria-label="Pick font color"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-800">Corners</span>
                  <select
                    value={borderRadius}
                    onChange={(e) => setBorderRadius(Number(e.target.value))}
                    className="px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                  >
                    <option value={0}>Square</option>
                    <option value={4}>Slightly rounded</option>
                    <option value={8}>Rounded</option>
                    <option value={16}>More rounded</option>
                    <option value={50}>Pill</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Open in new window checkbox */}
            <div className="flex items-center gap-3 mb-5">
              <button
                onClick={() => setOpenInNewWindow(!openInNewWindow)}
                className={`w-5 h-5 rounded flex items-center justify-center border-2 transition-colors ${
                  openInNewWindow 
                    ? 'bg-blue-500 border-blue-500' 
                    : 'bg-white border-gray-300 hover:border-gray-400'
                }`}
              >
                {openInNewWindow && <CheckIcon className="w-3.5 h-3.5 text-white" />}
              </button>
              <span className="text-sm text-gray-700">Open the survey in a new window</span>
            </div>

            {/* Copy the code section */}
            <div className="mb-5">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Copy the code</span>
                <button
                  onClick={handleCopy}
                  className="px-4 py-2 bg-gray-900 text-white text-sm font-medium rounded-lg hover:bg-gray-800 transition-colors"
                >
                  {isCopied ? 'Copied!' : 'Copy'}
                </button>
              </div>
              
              {/* Code block */}
              <div className="bg-gray-900 rounded-lg p-3 max-h-32 overflow-y-auto">
                <pre className="text-gray-300 text-xs whitespace-pre-wrap break-all font-mono leading-relaxed">
                  {embedCode}
                </pre>
              </div>
            </div>

            {/* Back button */}
            <div className="flex items-center gap-3 mb-8">
              <button
                onClick={onClose}
                className="px-6 py-2.5 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Back
              </button>
            </div>
          </div>
        </div>

        {/* Color Picker Popup */}
        {(showBgColorPicker || showFontColorPicker) && (
          <>
            <div 
              className="fixed inset-0 z-40" 
              onClick={() => {
                setShowBgColorPicker(false);
                setShowFontColorPicker(false);
                setActiveColorPicker(null);
              }}
            />
            <div 
              className="fixed z-50" 
              style={{ top: `${colorPickerPosition.top}px`, left: `${colorPickerPosition.left}px` }}
            >
              <div className="button-color-picker w-[280px] p-3 bg-white rounded-xl shadow-lg border border-gray-200">
                <style>{`
                  .button-color-picker .chrome-picker input[type='text'] { display: none !important; }
                  .button-color-picker .chrome-picker input[type='number'] { display: none !important; }
                  .button-color-picker .chrome-picker label { display: none !important; }
                  .button-color-picker .chrome-picker .chrome-fields { display: none !important; }
                  .button-color-picker .chrome-picker .flexbox-fix input { display: none !important; }
                  .button-color-picker .chrome-picker .flexbox-fix svg { display: none !important; }
                  .button-color-picker .chrome-picker .flexbox-fix { padding-bottom: 0 !important; margin-bottom: 0 !important; }
                  .button-color-picker .chrome-picker .hue-horizontal { margin-bottom: 0 !important; }
                  .button-color-picker .chrome-picker > div:last-child { margin-bottom: 0 !important; padding-bottom: 0 !important; }
                  .button-color-picker .chrome-picker { margin-bottom: 0 !important; padding-bottom: 0 !important; border-radius: 8px !important; overflow: hidden !important; }
                  .button-color-picker .chrome-picker .chrome-body { padding-bottom: 0 !important; margin-bottom: 0 !important; }
                  .button-color-picker .chrome-picker .saturation-relative { border-radius: 8px !important; overflow: hidden !important; }
                `}</style>
                <ChromePicker
                  disableAlpha={true}
                  styles={{
                    default: {
                      picker: { boxShadow: 'none', width: '100%' },
                      fields: { display: 'none' },
                      color: { display: 'none' },
                      activeColor: { display: 'none' },
                      hue: { marginTop: 0, marginBottom: 0 },
                      body: { paddingBottom: 0 },
                    },
                  }}
                  color={pickerColor}
                  onChange={(c) => {
                    const { r, g, b } = c.rgb || {};
                    const toHex = (n) => n.toString(16).padStart(2, '0');
                    const hex = `#${toHex(r)}${toHex(g)}${toHex(b)}`;
                    setPickerColor(hex);
                  }}
                />
                <div className="mt-1 flex items-center gap-2">
                  <div className="text-sm text-gray-600">Hex</div>
                  <input
                    className="ml-auto text-black text-sm px-3 py-2 border border-gray-200 rounded-md focus:ring-2 focus:ring-black focus:border-black text-left max-w-[180px]"
                    value={(pickerColor || '#ffffff').toUpperCase()}
                    onChange={(e) => {
                      const value = e.target.value.startsWith('#') ? e.target.value : `#${e.target.value}`;
                      setPickerColor(value);
                    }}
                  />
                </div>
                <button
                  onClick={handleColorConfirm}
                  disabled={!/^#[0-9A-F]{6}$/i.test(pickerColor)}
                  className="w-full mt-3 px-4 py-2 bg-black text-white text-sm font-medium rounded-lg hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Select
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Right side - Preview/Illustration (Pro plan gradient) - hidden on mobile, 1/3 on desktop */}
      <div className="hidden md:flex w-1/3 bg-gradient-to-b from-[#EFFBFF] to-[#FEE135] flex-col relative overflow-hidden">
        {/* Soft ambient glow */}
        <div className="absolute -top-16 -right-10 w-64 h-64 bg-white/40 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute bottom-0 -left-12 w-56 h-56 bg-white/30 rounded-full blur-3xl pointer-events-none" />

        {/* Close button */}
        <div className="absolute top-6 right-6 z-10">
          <button
            onClick={onClose}
            className="w-12 h-12 flex items-center justify-center bg-black/5 backdrop-blur-sm rounded-xl hover:bg-black/10 transition-colors"
          >
            <X className="w-6 h-6 text-gray-700" />
          </button>
        </div>
        
        {/* Illustration - centered in the middle of the panel */}
        <div className="flex-1 flex flex-col items-center justify-center px-8 py-12 relative z-[1]">
          <div className="w-full max-w-[240px]">
            {/* Laptop mockup */}
            <div className="bg-gradient-to-b from-gray-700 to-gray-800 rounded-t-xl p-2.5 w-full shadow-2xl ring-1 ring-white/10">
              <div className="bg-gray-800 rounded-md h-36 relative overflow-hidden">
                {/* Browser bar */}
                <div className="h-5 bg-gray-700/80 flex items-center gap-1 px-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-red-400/70" />
                  <span className="w-1.5 h-1.5 rounded-full bg-yellow-400/70" />
                  <span className="w-1.5 h-1.5 rounded-full bg-green-400/70" />
                  <div className="ml-2 h-2 flex-1 max-w-[70%] bg-gray-600/60 rounded-full" />
                </div>
                {/* Browser content (white page) */}
                <div className="absolute inset-x-2 top-7 bottom-2 bg-white rounded flex flex-col items-center justify-center px-3">
                  {/* faint page content */}
                  <div className="w-full space-y-1.5 mb-3 opacity-60">
                    <div className="h-1 bg-gray-200 rounded w-2/3 mx-auto" />
                    <div className="h-1 bg-gray-200 rounded w-full" />
                    <div className="h-1 bg-gray-200 rounded w-5/6 mx-auto" />
                  </div>
                  {/* Live button preview */}
                  <div
                    className="text-[7px] font-bold px-2.5 py-1 shadow-md whitespace-nowrap max-w-full overflow-hidden text-ellipsis"
                    style={{ backgroundColor: backgroundColor, color: fontColor, borderRadius: `${Math.min(borderRadius, 12)}px` }}
                  >
                    {buttonText || 'Start Survey'}
                  </div>
                </div>
              </div>
            </div>
            {/* Laptop base */}
            <div className="bg-gradient-to-b from-gray-600 to-gray-700 rounded-b-xl h-2.5 w-full" />
            <div className="bg-gray-500/80 h-1.5 w-3/5 mx-auto rounded-b-lg" />
          </div>
          
          {/* Description - below laptop */}
          <div className="mt-10 text-gray-700 text-center max-w-[260px]">
            <p className="text-sm leading-relaxed">
              <span className="font-semibold">Button</span> creates a clickable link styled as a button that directs users to your survey. 
              Customize colors and text to match your brand.
            </p>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

// Full-screen Pop-up Settings Modal (rendered via portal)
const PopupSettingsModal = ({ isOpen, onClose, surveyLink }) => {
  const [openOnLoad, setOpenOnLoad] = useState(true);
  const [isCopied, setIsCopied] = useState(false);

  // Use the production URL in deployed builds, but keep localhost while developing.
  const productionSurveyUrl = resolveEmbedSurveyUrl(surveyLink);
  
  const embedCode = `<!-- PollBolt Pop-up -->
<script>
(function() {
  var config = {
    surveyUrl: "${escapeForJS(productionSurveyUrl)}",
    openOnLoad: ${openOnLoad},
    width: 500,
    height: 600
  };
  
  // Create overlay
  var overlay = document.createElement('div');
  overlay.id = 'pollbolt-popup-overlay';
  overlay.style.cssText = 'position:fixed;inset:0;background:rgba(0,0,0,0.5);z-index:9998;display:none;align-items:center;justify-content:center;';
  
  // Create popup container
  var popup = document.createElement('div');
  popup.id = 'pollbolt-popup';
  popup.style.cssText = 'background:#fff;border-radius:12px;box-shadow:0 25px 50px -12px rgba(0,0,0,0.25);width:' + config.width + 'px;max-width:90vw;height:' + config.height + 'px;max-height:90vh;position:relative;overflow:hidden;';
  
  // Create close button
  var closeBtn = document.createElement('button');
  closeBtn.innerHTML = '&times;';
  closeBtn.style.cssText = 'position:absolute;top:12px;right:12px;width:32px;height:32px;border:none;background:rgba(0,0,0,0.1);border-radius:50%;cursor:pointer;font-size:20px;color:#666;z-index:1;display:flex;align-items:center;justify-content:center;';
  closeBtn.onmouseover = function() { this.style.background = 'rgba(0,0,0,0.2)'; };
  closeBtn.onmouseout = function() { this.style.background = 'rgba(0,0,0,0.1)'; };
  
  // Create iframe
  var iframe = document.createElement('iframe');
  iframe.src = config.surveyUrl;
  iframe.style.cssText = 'width:100%;height:100%;border:none;';
  iframe.title = 'Survey';
  
  popup.appendChild(closeBtn);
  popup.appendChild(iframe);
  overlay.appendChild(popup);
  document.body.appendChild(overlay);
  
  function showPopup() { overlay.style.display = 'flex'; }
  function hidePopup() { overlay.style.display = 'none'; }
  
  closeBtn.onclick = hidePopup;
  overlay.onclick = function(e) { if (e.target === overlay) hidePopup(); };
  
  if (config.openOnLoad) {
    if (document.readyState === 'complete') showPopup();
    else window.addEventListener('load', showPopup);
  }
  
  window.PollBoltPopup = { show: showPopup, hide: hidePopup };
})();
</script>`;

  const handleCopy = async () => {
    await navigator.clipboard.writeText(embedCode);
    setIsCopied(true);
    setTimeout(() => setIsCopied(false), 2000);
  };

  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-[10003] flex">
      {/* Left side - Settings (white background) - full width on mobile, 2/3 on desktop */}
      <div className="w-full md:w-2/3 bg-white flex flex-col relative">
        {/* Back button - top left corner */}
        <div className="absolute top-4 md:top-6 left-4 md:left-6 z-10">
          <button
            onClick={onClose}
            className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <ChevronLeft className="w-5 h-5 md:w-6 md:h-6 text-gray-600" />
          </button>
        </div>

        {/* Close button - top right corner on mobile only */}
        <div className="absolute top-4 right-4 z-10 md:hidden">
          <button
            onClick={onClose}
            className="w-10 h-10 flex items-center justify-center bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
          >
            <X className="w-5 h-5 text-gray-600" />
          </button>
        </div>
        
        {/* Content - centered with max-width */}
        <div className="flex-1 overflow-y-auto flex justify-center pt-24 md:pt-28 px-4 md:px-0">
          <div className="w-full max-w-2xl md:px-8 md:ml-32">
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6 md:mb-8">Pop-up window settings</h1>
            
            {/* Open on load toggle */}
            <div className="flex items-center justify-between mb-6 pb-6 border-b border-gray-200">
              <span className="text-sm font-medium text-gray-800">Open on page load at once</span>
              <button
                onClick={() => setOpenOnLoad(!openOnLoad)}
                className={`relative w-12 h-6 rounded-full transition-colors ${
                  openOnLoad ? 'bg-blue-500' : 'bg-gray-300'
                }`}
              >
                <span
                  className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-transform ${
                    openOnLoad ? 'left-7' : 'left-1'
                  }`}
                />
              </button>
            </div>

            {/* Instructions */}
            <div className="mb-6">
              <p className="text-sm text-gray-600 mb-4">
                Insert the code to the HTML of your web page
              </p>
              
              {/* Code block - Copy header stays fixed while the code scrolls */}
              <div className="bg-gray-900 rounded-lg overflow-hidden">
                <div className="flex items-center justify-between px-4 py-2.5 border-b border-white/10 bg-gray-900">
                  <button
                    onClick={handleCopy}
                    className="flex items-center gap-2 text-blue-400 hover:text-blue-300 transition-colors"
                  >
                    {isCopied ? (
                      <>
                        <CheckIcon className="w-4 h-4" />
                        <span className="text-sm font-medium">Copied!</span>
                      </>
                    ) : (
                      <>
                        <Copy className="w-4 h-4" />
                        <span className="text-sm font-medium">Copy</span>
                      </>
                    )}
                  </button>
                </div>
                <div className="max-h-48 overflow-y-auto p-4">
                  <pre className="text-gray-300 text-xs whitespace-pre-wrap break-all font-mono leading-relaxed">
                    {embedCode}
                  </pre>
                </div>
              </div>
            </div>

            {/* Back button */}
            <div className="flex items-center gap-3 mb-8">
              <button
                onClick={onClose}
                className="px-6 py-2.5 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Back
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Right side - Illustration (dark background like competitor) - hidden on mobile, 1/3 on desktop */}
      <div className="hidden md:flex w-1/3 bg-gradient-to-br from-gray-900 via-gray-900 to-gray-800 flex-col relative overflow-hidden">
        {/* Soft ambient glow */}
        <div className="absolute -top-16 -right-10 w-64 h-64 bg-blue-500/10 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute bottom-0 -left-12 w-56 h-56 bg-blue-400/5 rounded-full blur-3xl pointer-events-none" />

        {/* Close button */}
        <div className="absolute top-6 right-6 z-10">
          <button
            onClick={onClose}
            className="w-12 h-12 flex items-center justify-center bg-white/10 backdrop-blur-sm rounded-xl hover:bg-white/20 transition-colors"
          >
            <X className="w-6 h-6 text-white" />
          </button>
        </div>
        
        {/* Illustration - centered in the middle of the panel */}
        <div className="flex-1 flex flex-col items-center justify-center px-8 py-12 relative z-[1]">
          <div className="w-full max-w-[240px]">
            {/* Laptop mockup with popup */}
            <div className="bg-gradient-to-b from-gray-700 to-gray-800 rounded-t-xl p-2.5 w-full shadow-2xl ring-1 ring-white/5">
              <div className="bg-gray-800 rounded-md h-36 relative overflow-hidden">
                {/* Browser bar */}
                <div className="h-5 bg-gray-700/80 flex items-center gap-1 px-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-red-400/70" />
                  <span className="w-1.5 h-1.5 rounded-full bg-yellow-400/70" />
                  <span className="w-1.5 h-1.5 rounded-full bg-green-400/70" />
                  <div className="ml-2 h-2 flex-1 max-w-[70%] bg-gray-600/60 rounded-full" />
                </div>
                {/* Browser content */}
                <div className="absolute inset-x-2 top-7 bottom-2 bg-gray-600/60 rounded">
                  {/* faint page content behind the popup */}
                  <div className="p-2 space-y-1.5 opacity-40">
                    <div className="h-1 bg-gray-400 rounded w-2/3" />
                    <div className="h-1 bg-gray-400 rounded w-full" />
                    <div className="h-1 bg-gray-400 rounded w-5/6" />
                  </div>
                  {/* Popup overlay simulation */}
                  <div className="absolute inset-0 bg-black/40 backdrop-blur-[1px] rounded flex items-center justify-center">
                    {/* Popup window */}
                    <div className="bg-white rounded-lg w-[4.5rem] h-[5.5rem] shadow-[0_8px_24px_rgba(0,0,0,0.45)] ring-1 ring-black/5 flex flex-col overflow-hidden">
                      <div className="bg-blue-500 h-4 flex items-center justify-end px-1.5">
                        <div className="w-1.5 h-1.5 bg-white/70 rounded-full" />
                      </div>
                      <div className="flex-1 p-1.5 space-y-1">
                        <div className="h-1 bg-gray-300 rounded" />
                        <div className="h-1 bg-gray-200 rounded w-3/4" />
                        <div className="h-1 bg-gray-200 rounded w-1/2" />
                        <div className="mt-1.5 h-2 bg-blue-500/80 rounded w-2/3" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {/* Laptop base */}
            <div className="bg-gradient-to-b from-gray-600 to-gray-700 rounded-b-xl h-2.5 w-full" />
            <div className="bg-gray-500/80 h-1.5 w-3/5 mx-auto rounded-b-lg" />
          </div>
          
          {/* Description - below laptop */}
          <div className="mt-10 text-white text-center max-w-[260px]">
            <p className="text-sm leading-relaxed text-gray-300">
              <span className="font-semibold text-white">Pop-up window</span> with a survey will appear 
              above the contents of your web page after the page is loaded. It is not a permanent 
              element of the website.
            </p>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

const ShareSurveyModal = ({
  surveyId,
  surveyLink,
  handleCopyLink,
  onClose,
}) => {
  const [isCopied, setIsCopied] = useState(false);
  const [isEmbedCopied, setIsEmbedCopied] = useState(false);
  const [expandedSection, setExpandedSection] = useState(null);
  const [showIframeSettings, setShowIframeSettings] = useState(false);
  const [showWidgetSettings, setShowWidgetSettings] = useState(false);
  const [isLinkFocused, setIsLinkFocused] = useState(false);
  const linkInputRef = useRef(null);
  const [showButtonSettings, setShowButtonSettings] = useState(false);
  const [showPopupSettings, setShowPopupSettings] = useState(false);

  const handleCopy = () => {
    setIsCopied(true);
    handleCopyLink();
    setTimeout(() => {
      setIsCopied(false);
    }, 3000);
  };

  const handleEmbedCopy = async () => {
    setIsEmbedCopied(true);
    await navigator.clipboard.writeText(`<iframe src="${escapeForHTML(surveyLink)}" width="100%" height="600" frameborder="0" allowfullscreen></iframe>`);
    setTimeout(() => {
      setIsEmbedCopied(false);
    }, 3000);
  };

  const handleShareOnSocialMedia = async (name) => {
    const sharerUrls = {
      Facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
        `https://app.pollbolt.com/s/${surveyId}`
      )}`,
      X: `https://twitter.com/intent/tweet?url=${encodeURIComponent(
        `https://app.pollbolt.com/s/${surveyId}`
      )}&text=${encodeURIComponent("")}`,
      LinkedIn: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(
        `https://app.pollbolt.com/s/${surveyId}`
      )}`,
    };
    window.open(sharerUrls[name], "_blank", "noopener,noreferrer");
  };

  const downloadQRCode = async (format) => {
    const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(surveyLink)}`;
    
    try {
      const response = await fetch(qrUrl);
      const blob = await response.blob();
      
      if (format === 'svg') {
        const svgUrl = `https://api.qrserver.com/v1/create-qr-code/?size=300x300&format=svg&data=${encodeURIComponent(surveyLink)}`;
        window.open(svgUrl, '_blank');
        return;
      }
      
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `qr-code.${format}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error downloading QR code:', error);
    }
  };

  const toggleSection = (section) => {
    setExpandedSection(expandedSection === section ? null : section);
  };

  // X (Twitter) icon component
  const XIcon = ({ className }) => (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
    </svg>
  );

  return (
    <>
      <div className="px-1 pb-4">
        {/* Share the form link */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-600 mb-2">
            Share the form link
          </label>
          <div 
            onClick={() => {
              handleCopy();
              // Select the text in the input
              if (linkInputRef.current) {
                linkInputRef.current.select();
              }
              setIsLinkFocused(true);
            }}
            className={`flex items-center gap-2 p-2 pl-4 border rounded-xl bg-white cursor-pointer transition-all
              ${isLinkFocused 
                ? 'border-gray-300 ring-2 ring-[#f9f3a8] ring-offset-0' 
                : 'border-gray-300 hover:border-gray-400'
              }`}
          >
            <input
              ref={linkInputRef}
              type="text"
              value={surveyLink}
              readOnly
              onFocus={() => setIsLinkFocused(true)}
              onBlur={() => setIsLinkFocused(false)}
              className="truncate flex-1 text-sm text-gray-600 min-w-0 bg-transparent outline-none cursor-pointer"
            />
            <Button
              variant="default"
              className="flex-shrink-0 text-sm rounded-lg pointer-events-none"
            >
              {isCopied ? "Copied" : "Copy"}
            </Button>
          </div>
        </div>

        {/* Collapsible Sections */}
        <div className="space-y-1">
          {/* Display survey on your website */}
          <div className="rounded-xl overflow-hidden">
            <button
              onClick={() => toggleSection('embed')}
              className="w-full flex items-center justify-between p-3 hover:bg-gray-50 transition-colors rounded-xl"
            >
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-[#DBEAFE] flex items-center justify-center">
                  <Globe className="w-5 h-5 text-[#1E3A8A]" />
                </div>
                <span className="text-sm font-medium text-gray-800">Display form on your website</span>
              </div>
              <ChevronRight 
                className={`w-5 h-5 text-gray-400 transition-transform duration-200 ${
                  expandedSection === 'embed' ? 'rotate-90' : ''
                }`}
              />
            </button>
            
            {expandedSection === 'embed' && (
              <div className="px-3 pb-3 pt-1">
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { icon: Code, label: 'Iframe', action: () => setShowIframeSettings(true) },
                    { icon: LayoutGrid, label: 'Widget', action: () => setShowWidgetSettings(true) },
                    { icon: MessageSquare, label: 'Pop-up', action: () => setShowPopupSettings(true) },
                    { icon: MousePointer, label: 'Button', action: () => setShowButtonSettings(true) },
                  ].map((option) => (
                    <button
                      key={option.label}
                      onClick={option.action}
                      className="flex flex-col items-center justify-center p-3 bg-gray-50 rounded-xl hover:bg-blue-50 transition-colors"
                    >
                      <option.icon className="w-5 h-5 text-gray-600 mb-1.5" />
                      <span className="text-xs text-gray-700">{option.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Publish on social media */}
          <div className="rounded-xl overflow-hidden">
            <button
              onClick={() => toggleSection('social')}
              className="w-full flex items-center justify-between p-3 hover:bg-gray-50 transition-colors rounded-xl"
            >
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-[#E9D5FF] flex items-center justify-center">
                  <Share2 className="w-5 h-5 text-[#581C87]" />
                </div>
                <span className="text-sm font-medium text-gray-800">Publish form on social media</span>
              </div>
              <ChevronRight 
                className={`w-5 h-5 text-gray-400 transition-transform duration-200 ${
                  expandedSection === 'social' ? 'rotate-90' : ''
                }`}
              />
            </button>
            
            {expandedSection === 'social' && (
              <div className="px-3 pb-3 pt-1">
                <div className="flex gap-2">
                  {[
                    { icon: Facebook, name: 'Facebook', color: 'text-blue-600 bg-blue-50 hover:bg-blue-100' },
                    { icon: XIcon, name: 'X', color: 'text-gray-900 bg-gray-100 hover:bg-gray-200' },
                    { icon: Linkedin, name: 'LinkedIn', color: 'text-blue-700 bg-blue-50 hover:bg-blue-100' },
                  ].map((social) => (
                    <button
                      key={social.name}
                      onClick={() => handleShareOnSocialMedia(social.name)}
                      className={`flex-1 flex items-center justify-center p-3 rounded-xl transition-colors ${social.color}`}
                      title={`Share on ${social.name}`}
                    >
                      <social.icon className="w-5 h-5" />
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Download QR code */}
          <div className="rounded-xl overflow-hidden">
            <button
              onClick={() => toggleSection('qr')}
              className="w-full flex items-center justify-between p-3 hover:bg-gray-50 transition-colors rounded-xl"
            >
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-[#FDE68A] flex items-center justify-center">
                  <QrCode className="w-5 h-5 text-[#78350F]" />
                </div>
                <span className="text-sm font-medium text-gray-800">Download QR code</span>
              </div>
              <ChevronRight 
                className={`w-5 h-5 text-gray-400 transition-transform duration-200 ${
                  expandedSection === 'qr' ? 'rotate-90' : ''
                }`}
              />
            </button>
            
            {expandedSection === 'qr' && (
              <div className="px-3 pb-3 pt-1">
                <div className="flex gap-2">
                  {['JPG', 'PNG', 'SVG'].map((format) => (
                    <button
                      key={format}
                      onClick={() => downloadQRCode(format.toLowerCase())}
                      className="flex-1 py-2.5 px-3 bg-gray-50 rounded-xl text-sm font-medium text-gray-700 hover:bg-amber-50 transition-colors"
                    >
                      {format}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Iframe Settings Full Screen Modal */}
      <IframeSettingsModal 
        isOpen={showIframeSettings}
        onClose={() => setShowIframeSettings(false)}
        surveyLink={surveyLink}
      />

      {/* Widget Settings Full Screen Modal */}
      <WidgetSettingsModal 
        isOpen={showWidgetSettings}
        onClose={() => setShowWidgetSettings(false)}
        surveyLink={surveyLink}
      />

      {/* Button Settings Full Screen Modal */}
      <ButtonSettingsModal 
        isOpen={showButtonSettings}
        onClose={() => setShowButtonSettings(false)}
        surveyLink={surveyLink}
      />

      {/* Popup Settings Full Screen Modal */}
      <PopupSettingsModal 
        isOpen={showPopupSettings}
        onClose={() => setShowPopupSettings(false)}
        surveyLink={surveyLink}
      />
    </>
  );
};

export default ShareSurveyModal;
